import { forwardRef, useContext } from 'react';
import type { FunctionComponent, PropsWithoutRef, ReactElement, Ref } from 'react';
import styled from 'styled-components';

import { Tag, isValidElement } from '@pega/cosmos-react-core';
import type { ForwardProps } from '@pega/cosmos-react-core';
import { getXMLAttributes } from '@pega/cosmos-react-rte';

import { FeedContext } from '../Feed/Feed.context';

interface HashtagButtonProps {
  /** Text for the button, or an object containing all necessary props. */
  children: Record<string, string | boolean> | ReactElement;
  /** The tag value. */
  tag?: string;
}

const StyledTag = styled(Tag)`
  cursor: pointer;
`;

const serializer = new XMLSerializer();

const HashtagButton: FunctionComponent<HashtagButtonProps & ForwardProps> = forwardRef(
  function HashtagButton(
    { children, tag: tagProp = '', ...restProps }: PropsWithoutRef<HashtagButtonProps>,
    ref: Ref<HTMLButtonElement>
  ) {
    const { onTagClick } = useContext(FeedContext);

    let tag = '';
    let bold = false;
    let italic = false;
    let lineThrough = false;
    let code = false;

    if (!isValidElement(children)) {
      if (!children.tag) return <span>{children.rawText}</span>;
      tag = String(children.tag);
      bold = !!children.bold;
      italic = !!children.italic;
      lineThrough = !!children['line-through'];
      code = !!children.code;
    } else {
      tag = tagProp;
    }

    let formattedTag: string | JSX.Element = tag;
    if (code) formattedTag = <code>{formattedTag}</code>;
    if (lineThrough) formattedTag = <del>{formattedTag}</del>;
    if (italic) formattedTag = <em>{formattedTag}</em>;
    if (bold) formattedTag = <strong>{formattedTag}</strong>;

    return (
      <StyledTag {...restProps} ref={ref} onClick={() => onTagClick?.(tag)} spellCheck={false}>
        {isValidElement(children) ? children : formattedTag}
      </StyledTag>
    );
  }
);

export const HashtagButtonConfig = {
  type: 'tag',
  xmlElement: 'pega-tag',
  regexPattern: /(?:^|)<pega-tag (?:[^\n/]|\/(?!>))+\/>/gm,
  component: HashtagButton,
  inject: ({ tag }: { tag: string }) => {
    if (!tag) return;

    const element = document.createElementNS(null, 'pega-tag');
    element.setAttribute('tag', tag);

    return serializer.serializeToString(element);
  },
  extract: (element: string) => {
    const attributes = getXMLAttributes(element);

    if (!attributes || !attributes.tag) return undefined;
    return { tag: attributes.tag };
  }
};

export default HashtagButton;
